import { EXPLORE_CORRIDORS_TAB, GUEST_EXPLORE_CORRIDORS_TAB } from "@/pages/discover/discoverExplore.const";

/** Uniform height for corridor promo, placeholder and head cards in carousels and explore rows. */
export const ECOSYSTEM_CORRIDOR_CARD_HEIGHT = 300;

export const ECOSYSTEM_CORRIDOR_PROMO_TITLE = "Join an Ecosystem";
export const ECOSYSTEM_CORRIDOR_PROMO_BODY =
  "Join industry and regional ecosystems to connect with verified companies.";
export const ECOSYSTEM_CORRIDOR_PROMO_CTA = "Explore Ecosystems Corridor";

export const LEGACY_EXPERTS_AND_CORRIDORS_PATH = "/experts-and-corridors";

export function ecosystemCorridorPromoHref(isAuthenticated: boolean): string {
  return isAuthenticated ? EXPLORE_CORRIDORS_TAB : GUEST_EXPLORE_CORRIDORS_TAB;
}

/** Fixed size for cards in the global corridor explore horizontal rows. */
export const ECOSYSTEM_EXPLORE_CARD_WIDTH = 300;
export const ECOSYSTEM_EXPLORE_CARD_HEIGHT = 300;
/** Compact stacked corridor card (thumbnail + title + CTA). */
export const ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT = 136;
export const ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT = 48;
export const ECOSYSTEM_EXPLORE_CARD_GRID_GAP = 24;

/**
 * Container-query breakpoints (keys are the grid's own available width, not the viewport).
 * Keeps each card near ~300px wide and wraps remaining cards to the next line when the
 * available space (e.g. with the sidebar expanded) is narrow. Up to 5 cards per row on a wide container.
 * NOTE: consumers must render the grid with `type="container"`.
 */
export const ECOSYSTEM_EXPLORE_CARD_GRID_COLS = {
  base: 1,
  "620px": 2,
  "940px": 3,
  "1260px": 4,
  "1580px": 5,
} as const;

/** Up to four equal-width cards per row with no gap in Experts & Corridors sections. */
export const ECOSYSTEM_EXPLORE_STRIP_GRID_COLS = {
  base: 1,
  "480px": 2,
  "720px": 3,
  "960px": 4,
} as const;

/**
 * Compact corridor explore cards. Caps at 3 columns so a wide container
 * shows a clean 3-card row with larger cells (~400px+ per card).
 */
export const CORRIDOR_EXPLORE_GRID_COLS = {
  base: 1,
  "700px": 2,
  "1050px": 3,
} as const;

const CORRIDOR_COL_BREAKPOINTS: { minWidth: number; columns: number }[] = [
  { minWidth: 1050, columns: 3 },
  { minWidth: 700, columns: 2 },
];

/** Column count for corridor explore grid (container width, same as SimpleGrid container queries). */
export function corridorExploreColumnCount(widthPx: number): number {
  if (!Number.isFinite(widthPx) || widthPx <= 0) return CORRIDOR_EXPLORE_GRID_COLS.base;
  for (const bp of CORRIDOR_COL_BREAKPOINTS) {
    if (widthPx >= bp.minWidth) return bp.columns;
  }
  return CORRIDOR_EXPLORE_GRID_COLS.base;
}

export const ECOSYSTEM_CORRIDOR_HEAD_CONTACT_EMAIL = "team@gtsetu.com";

export const ECOSYSTEM_CORRIDOR_HEAD_MAILTO = `mailto:${ECOSYSTEM_CORRIDOR_HEAD_CONTACT_EMAIL}?subject=${encodeURIComponent("Ecosystem Corridor Head Inquiry")}`;

export type EcosystemCorridorHeadDetailSection = {
  title: string;
  body: string;
};

export const ECOSYSTEM_CORRIDOR_HEAD_DETAIL_SECTIONS: EcosystemCorridorHeadDetailSection[] = [
  {
    title: "HOW IT WORKS",
    body: "Whether you represent a sector, a business network or possess specialized expertise, companies can engage with your ecosystem to explore global partnerships, market-entry opportunities, distribution channels, technology collaborations, sourcing relationships and strategic business connections.",
  },
  {
    title: "HOW YOU EARN",
    body: "Generate opportunities through your expertise and ecosystem.",
  },
  {
    title: "REQUIREMENTS",
    body: "We look for experienced professionals, consultants, industry leaders, associations, chambers and ecosystem builders with established sector knowledge and business networks.",
  },
];

export type CorridorListEntry<T> =
  | { type: "item"; item: T; key: string }
  | { type: "promo"; key: string };

export type BuildCorridorEntriesOptions = {
  /** Insert one promo card after every N items (default 2). Use 0 or less to disable. */
  promoEveryN?: number;
};

function interleavePromoEveryN<T>(
  items: T[],
  itemKey: (item: T) => string,
  everyN: number,
): CorridorListEntry<T>[] {
  const result: CorridorListEntry<T>[] = [];

  items.forEach((item, index) => {
    result.push({ type: "item", item, key: itemKey(item) });
    if ((index + 1) % everyN === 0) {
      result.push({ type: "promo", key: `promo-after-${index}` });
    }
  });

  return result;
}

function mapItemsOnly<T>(items: T[], itemKey: (item: T) => string): CorridorListEntry<T>[] {
  return items.map((item) => ({ type: "item" as const, item, key: itemKey(item) }));
}

export function buildEcosystemCorridorEntries<T>(
  realItems: T[],
  placeholderItems: T[],
  itemKey: (item: T) => string,
  options?: BuildCorridorEntriesOptions,
): CorridorListEntry<T>[] {
  const rawEveryN = options?.promoEveryN;
  const promoDisabled = rawEveryN !== undefined && rawEveryN <= 0;

  if (promoDisabled) {
    return mapItemsOnly([...realItems, ...placeholderItems], itemKey);
  }

  const promoEveryN = Math.max(1, rawEveryN ?? 2);

  if (realItems.length === 0) {
    const result: CorridorListEntry<T>[] = [{ type: "promo", key: "promo" }];
    for (const item of placeholderItems) {
      result.push({ type: "item", item, key: itemKey(item) });
    }
    return result;
  }

  const liveEntries =
    realItems.length >= promoEveryN
      ? interleavePromoEveryN(realItems, itemKey, promoEveryN)
      : [
          ...mapItemsOnly(realItems, itemKey),
          { type: "promo" as const, key: "promo" },
        ];

  return [...liveEntries, ...mapItemsOnly(placeholderItems, itemKey)];
}

function corridorDisplayTitle(head: {
  corridorName?: string | null;
  corridorLabel?: string | null;
  name?: string | null;
}): string {
  return (head.corridorName ?? "").trim() || (head.corridorLabel ?? "").trim() || (head.name ?? "").trim();
}

function compareCorridorTitle(
  a: { corridorName?: string | null; corridorLabel?: string | null; name?: string | null },
  b: { corridorName?: string | null; corridorLabel?: string | null; name?: string | null },
): number {
  return corridorDisplayTitle(a).localeCompare(corridorDisplayTitle(b), undefined, {
    sensitivity: "base",
  });
}

/** Live View cards A–Z, then Coming Soon A–Z. Pure; call at render time. */
export function sortCorridorHeadsForDisplay<
  T extends {
    profileStatus?: string | null;
    corridorName?: string | null;
    corridorLabel?: string | null;
    name?: string | null;
  },
>(items: T[]): T[] {
  const live: T[] = [];
  const comingSoon: T[] = [];
  for (const item of items) {
    if (item.profileStatus === "COMING_SOON") comingSoon.push(item);
    else live.push(item);
  }
  live.sort(compareCorridorTitle);
  comingSoon.sort(compareCorridorTitle);
  return [...live, ...comingSoon];
}
